<template>
  <span
    class="chart-thumbnail-image-container"
    ref="chartThumbnailImageContainerElement"
    :data-intersection-observer-element-id="intersectionObserverElementId"
  >
    <span
      v-show="!shouldDisplayImage || !hasImageBeenLoaded"
      class="chart-thumbnail-image-loading-spinner-container"
    >
      <i class="fad fa-fw fa-spinner-third fa-spin"></i>
    </span>
    <img
      v-if="shouldDisplayImage"
      v-show="hasImageBeenLoaded"
      :src="imgSrc"
      :alt="imgAlt"
      class="chart-thumbnail-image"
      @load="handleImageLoaded()"
      @error="handleImageLoaded()"
    />
  </span>
</template>

<script lang="ts" setup>
  import { ref, toRefs, computed, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useUniqueIds } from '@/composables/useUniqueIds';

  const { getPositiveUniqueId } = useUniqueIds();

  interface IntersectionObserverVisibleElementIds {
    [key: string]: boolean;
  }
  type IntersectionObserverFunction = (element: HTMLElement) => void;
  type IntersectionUnobserverFunction = (element: HTMLElement, intersectionObserverElementId: string) => void;

  const props = defineProps<{
    imgSrc: string;
    imgAlt: string;
    intersectionObserverVisibleElementIds: IntersectionObserverVisibleElementIds;
    observeIntersection: IntersectionObserverFunction;
    unobserveIntersection: IntersectionUnobserverFunction;
  }>();
  const { imgSrc, imgAlt, intersectionObserverVisibleElementIds } = toRefs(props);
  const intersectionObserverElementId = `${getPositiveUniqueId()}`;
  const isVisible = computed(() => {
    return !!intersectionObserverVisibleElementIds.value[intersectionObserverElementId];
  });
  const shouldDisplayImage = ref(false);
  let shouldDisplayImageTimer = -1;
  const MILLISECONDS_VISIBLE_BEFORE_IMAGE_STARTS_LOADING = 200;
  const chartThumbnailImageContainerElement = ref<HTMLSpanElement>();
  const hasImageBeenLoaded = ref(false);

  function handleIsVisibleChanged() {
    if (shouldDisplayImage.value) {
      return;
    }

    if (!isVisible.value) {
      window.clearTimeout(shouldDisplayImageTimer);
      return;
    }

    shouldDisplayImageTimer = window.setTimeout(() => {
      shouldDisplayImage.value = true;
    }, MILLISECONDS_VISIBLE_BEFORE_IMAGE_STARTS_LOADING);
  }

  function handleImageLoaded() {
    hasImageBeenLoaded.value = true;
  }

  watch(isVisible, handleIsVisibleChanged);

  handleIsVisibleChanged();

  onMounted(() => {
    if (!(chartThumbnailImageContainerElement.value instanceof HTMLElement)) {
      return;
    }

    props.observeIntersection(chartThumbnailImageContainerElement.value);

    onBeforeUnmount(() => {
      if (!(chartThumbnailImageContainerElement.value instanceof HTMLElement)) {
        return;
      }

      props.unobserveIntersection(chartThumbnailImageContainerElement.value, intersectionObserverElementId);
    });
  });
</script>

<style scoped>
  .chart-thumbnail-image-container {
    display: inline-block;
    height: calc(100% - 26px); /* 26px is the height margin-bottom of the .chart-thumbnail-name */
    width: 100%;
    padding: 0px;
    margin: 0px;
  }

  .chart-thumbnail-image,
  .chart-thumbnail-image-loading-spinner-container {
    display: inline-block;
    height: 100%;
    width: 100%;
    padding: 0px;
    margin: 0px;
  }

  .chart-thumbnail-image-loading-spinner-container {
    font-size: 36px;
  }

  .chart-thumbnail-image-loading-spinner-container i {
    position: relative;
    top: calc(50% - 18px); /* 18px is 0.5 * 36px, which is the font-size of the .chart-thumbnail-image-loading-spinner-container */
  }
</style>
